import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header17 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template17' },
        { label: 'Gallery', path: '/template17/shop' },
        { label: 'About', path: '/template17/about' },
        { label: 'Contact', path: '/template17/contact' },
        { label: 'Orders', path: '/template17/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@400;600;700&family=Inter:wght@300;400;500&display=swap');
            `}</style>

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.98)' : '#FFFFFF',
                    borderBottom: '1px solid #E4E4E7',
                    transition: 'all 0.3s ease',
                    backdropFilter: scrolled ? 'blur(10px)' : 'none',
                }}
            >
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '72px',
                    }}
                >
                    {/* Left: Brand */}
                    <h1
                        onClick={() => navigate('/template17')}
                        style={{
                            fontFamily: "'Source Serif 4', Georgia, serif",
                            fontSize: '22px',
                            fontWeight: 600,
                            color: '#18181B',
                            cursor: 'pointer',
                            letterSpacing: '3px',
                            margin: 0,
                            textTransform: 'uppercase',
                        }}
                    >
                        KALA
                    </h1>

                    {/* Center: Nav Links (Desktop) */}
                    <nav
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '40px',
                        }}
                        className="kala-desktop-nav"
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    fontFamily: "'Source Serif 4', Georgia, serif",
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    color: '#18181B',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase',
                                    transition: 'color 0.3s ease',
                                }}
                                onMouseEnter={(e) => (e.target.style.color = '#71717A')}
                                onMouseLeave={(e) => (e.target.style.color = '#18181B')}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right: Icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <Search
                            size={19}
                            style={{ color: '#18181B', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#71717A')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#18181B')}
                        />
                        <User
                            size={19}
                            style={{ color: '#18181B', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onClick={() => navigate('/template17/profile')}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#71717A')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#18181B')}
                        />
                        <div
                            style={{ position: 'relative', cursor: 'pointer' }}
                            onClick={() => navigate('/template17/cart')}
                        >
                            <ShoppingCart
                                size={19}
                                style={{ color: '#18181B', transition: 'color 0.3s ease' }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = '#71717A')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = '#18181B')}
                            />
                            {cartCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        backgroundColor: '#18181B',
                                        color: '#FFFFFF',
                                        fontSize: '10px',
                                        fontWeight: 500,
                                        fontFamily: "'Inter', sans-serif",
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            className="kala-mobile-menu-btn"
                            onClick={() => setMobileMenuOpen(true)}
                            style={{
                                display: 'none',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            <Menu size={22} style={{ color: '#18181B' }} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Full-Screen Overlay */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 100,
                    backgroundColor: '#FFFFFF',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: mobileMenuOpen ? 1 : 0,
                    pointerEvents: mobileMenuOpen ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease',
                }}
            >
                <button
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                        position: 'absolute',
                        top: '24px',
                        right: '24px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                    }}
                >
                    <X size={24} style={{ color: '#18181B' }} />
                </button>

                <nav style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '32px' }}>
                    {navLinks.map((link) => (
                        <button
                            key={link.label}
                            onClick={() => handleNavClick(link.path)}
                            style={{
                                fontFamily: "'Source Serif 4', Georgia, serif",
                                fontSize: '24px',
                                fontWeight: 400,
                                color: '#18181B',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                letterSpacing: '3px',
                                textTransform: 'uppercase',
                                transition: 'color 0.3s ease',
                            }}
                            onMouseEnter={(e) => (e.target.style.color = '#71717A')}
                            onMouseLeave={(e) => (e.target.style.color = '#18181B')}
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Responsive Styles */}
            <style>{`
                .kala-desktop-nav {
                    display: flex !important;
                }
                .kala-mobile-menu-btn {
                    display: none !important;
                }
                @media (max-width: 768px) {
                    .kala-desktop-nav {
                        display: none !important;
                    }
                    .kala-mobile-menu-btn {
                        display: block !important;
                    }
                }
            `}</style>
        </>
    );
};

export default Header17;
